import React, { useState } from 'react';
import { Download, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import { useApi } from '@/hooks';
import { User } from '@/types';

const ApiDemo: React.FC = () => {
  const [endpoint, setEndpoint] = useState('/users');
  const { data: users, loading, error, refetch } = useApi<User[]>(`https://jsonplaceholder.typicode.com${endpoint}`);

  const handleEndpointChange = (newEndpoint: string) => {
    setEndpoint(newEndpoint);
  };

  return (
    <div className="card">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">API Integration Demo</h2>
      
      {/* API Controls */}
      <div className="mb-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select API Endpoint
          </label>
          <div className="flex space-x-2">
            <button
              onClick={() => handleEndpointChange('/users')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                endpoint === '/users'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Users
            </button>
            <button
              onClick={() => handleEndpointChange('/posts')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                endpoint === '/posts'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Posts
            </button>
            <button
              onClick={() => handleEndpointChange('/comments')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                endpoint === '/comments'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Comments
            </button>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={refetch}
            disabled={loading}
            className="btn-primary flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>{loading ? 'Loading...' : 'Refresh'}</span>
          </button>
          
          {users && (
            <button
              onClick={() => {
                const dataStr = JSON.stringify(users, null, 2);
                const dataBlob = new Blob([dataStr], { type: 'application/json' });
                const url = URL.createObjectURL(dataBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${endpoint.slice(1)}.json`;
                link.click();
                URL.revokeObjectURL(url);
              }}
              className="btn-secondary flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Download JSON</span>
            </button>
          )}
        </div>
      </div>

      {/* Status Indicators */}
      <div className="mb-6">
        {loading && (
          <div className="flex items-center space-x-2 text-blue-600">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span className="text-sm">Loading data...</span>
          </div>
        )}
        
        {error && (
          <div className="flex items-center space-x-2 text-red-600">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">Error: {error}</span>
          </div>
        )}
        
        {users && !loading && !error && (
          <div className="flex items-center space-x-2 text-green-600">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">Data loaded successfully</span>
          </div>
        )}
      </div>

      {/* Data Display */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-3">
          Response Data ({endpoint})
        </h3>
        
        {loading ? (
          <div className="text-center py-8 text-gray-500">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading...
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-600">
            <AlertCircle className="w-6 h-6 mx-auto mb-2" />
            Failed to load data
          </div>
        ) : users ? (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {Array.isArray(users) ? (
              users.slice(0, 5).map((item: unknown, index: number) => (
                <div key={index} className="bg-white p-3 rounded border text-sm">
                  <pre className="whitespace-pre-wrap text-xs">
                    {JSON.stringify(item, null, 2)}
                  </pre>
                </div>
              ))
            ) : (
              <div className="bg-white p-3 rounded border text-sm">
                <pre className="whitespace-pre-wrap text-xs">
                  {JSON.stringify(users, null, 2)}
                </pre>
              </div>
            )}
            {Array.isArray(users) && users.length > 5 && (
              <div className="text-center text-gray-500 text-sm">
                ... and {users.length - 5} more items
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No data available
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiDemo;
